import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import Splash from "./pages/Splash";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Challenges from "./pages/Challenges";
import ChallengeDetail from "./pages/ChallengeDetail";
import SubmissionStatus from "./pages/SubmissionStatus";
import Progress from "./pages/Progress";
import Badges from "./pages/Badges";
import Leaderboard from "./pages/Leaderboard";
import Diet from "./pages/Diet";
import ExerciseLibrary from "./pages/ExerciseLibrary";
import ExerciseDetail from "./pages/ExerciseDetail";
import WorkoutPlan from "./pages/WorkoutPlan";
import Profile from "./pages/Profile";
import Survivors from "./pages/Survivors";
import Shop from "./pages/Shop";
import MyInvites from "./pages/MyInvites";
import ClaimInvite from "./pages/ClaimInvite";
import Parleys from "./pages/Parleys";
import ParleyDetail from "./pages/ParleyDetail";
import RunTracker from "./pages/RunTracker";
import RunDetail from "./pages/RunDetail";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminReview from "./pages/admin/AdminReview";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminChallenges from "./pages/admin/AdminChallenges";
import AdminBadges from "./pages/admin/AdminBadges";
import AdminInvites from "./pages/admin/AdminInvites";
import AdminInviteQuotas from "./pages/admin/AdminInviteQuotas";
import AdminQuests from "./pages/admin/AdminQuests";
import AdminShop from "./pages/admin/AdminShop";
import AppLayout from "./components/AppLayout";

function Protected({ children, role }) {
    const { user, loading } = useAuth();
    const loc = useLocation();
    if (loading)
        return (
            <div className="min-h-screen flex items-center justify-center bg-charcoal-900 text-neon-green font-display uppercase">
                Loading...
            </div>
        );
    if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
    if (role && user.role !== role) return <Navigate to="/app" replace />;
    return children;
}

function RootRedirect() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Splash />;
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (!user.goal) return <Navigate to="/onboarding" replace />;
    return <Navigate to="/app" replace />;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/claim/:code" element={<ClaimInvite />} />
            <Route
                path="/onboarding"
                element={
                    <Protected>
                        <Onboarding />
                    </Protected>
                }
            />
            <Route
                path="/app"
                element={
                    <Protected>
                        <AppLayout />
                    </Protected>
                }
            >
                <Route index element={<Dashboard />} />
                <Route path="challenges" element={<Challenges />} />
                <Route path="challenges/:id" element={<ChallengeDetail />} />
                <Route path="submissions/:id" element={<SubmissionStatus />} />
                <Route path="progress" element={<Progress />} />
                <Route path="badges" element={<Badges />} />
                <Route path="leaderboard" element={<Leaderboard />} />
                <Route path="diet" element={<Diet />} />
                <Route path="exercises" element={<ExerciseLibrary />} />
                <Route path="exercises/:id" element={<ExerciseDetail />} />
                <Route path="workout-plan" element={<WorkoutPlan />} />
                <Route path="profile" element={<Profile />} />
                <Route path="survivors" element={<Survivors />} />
                <Route path="shop" element={<Shop />} />
                <Route path="invites" element={<MyInvites />} />
                <Route path="parleys" element={<Parleys />} />
                <Route path="parleys/:id" element={<ParleyDetail />} />
                <Route path="runs" element={<RunTracker />} />
                <Route path="runs/:id" element={<RunDetail />} />
            </Route>
            <Route
                path="/admin"
                element={
                    <Protected role="admin">
                        <AdminLayout />
                    </Protected>
                }
            >
                <Route index element={<AdminDashboard />} />
                <Route path="review" element={<AdminReview />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="challenges" element={<AdminChallenges />} />
                <Route path="badges" element={<AdminBadges />} />
                <Route path="invites" element={<AdminInvites />} />
                <Route path="invite-quotas" element={<AdminInviteQuotas />} />
                <Route path="quests" element={<AdminQuests />} />
                <Route path="shop" element={<AdminShop />} />
            </Route>
        </Routes>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRoutes />
                <ToasterWrap />
            </AuthProvider>
        </BrowserRouter>
    );
}

function ToasterWrap() {
    return (
        <Toaster
                theme="dark"
                position="top-center"
                toastOptions={{
                    style: {
                        background: "#0d0d0d",
                        border: "1px solid #262626",
                        color: "#fff",
                        fontFamily: "DM Sans",
                    },
                }}
            />
    );
}
